import { __rest, __assign } from '../../_virtual/_tslib.js';
import React from 'react';
import { styled } from '../../stitches.config.js';
import { Box } from '../Box/Box.js';
import { Input } from '../Input/Input.js';
import { Label } from '../Label/Label.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import { ExclamationTriangleIcon, EyeOpenIcon, EyeClosedIcon, CrossCircledIcon } from '@radix-ui/react-icons';

// COMPONENTS
var AdornmentGroup = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center'
});
var StyledExclamationTriangleIcon = styled(ExclamationTriangleIcon, {
  '& + *': {
    marginLeft: '$1'
  }
});
var StyledEyeOpenIcon = styled(EyeOpenIcon, {
  '@hover': {
    '&:hover': {
      cursor: 'pointer'
    }
  }
});
var StyledEyeClosedIcon = styled(EyeClosedIcon, {
  '@hover': {
    '&:hover': {
      cursor: 'pointer'
    }
  }
});
var StyledCrossCircledIcon = styled(CrossCircledIcon, {
  '@hover': {
    '&:hover': {
      cursor: 'pointer'
    }
  }
});
var TextField = /*#__PURE__*/React.forwardRef(function (_a, forwardedRef) {
  var state = _a.state,
    clearable = _a.clearable,
    LabelOrComponent = _a.label,
    id = _a.id,
    type = _a.type,
    disabled = _a.disabled,
    readOnly = _a.readOnly,
    onBlur = _a.onBlur,
    onFocus = _a.onFocus,
    css = _a.css,
    props = __rest(_a, ["state", "clearable", "label", "id", "type", "disabled", "readOnly", "onBlur", "onFocus", "css"]);
  var inputRef = React.useRef(null);
  React.useImperativeHandle(forwardedRef, function () {
    return inputRef.current;
  });
  var _b = React.useState(false),
    inputHasFocus = _b[0],
    setInputHasFocus = _b[1];
  var _c = React.useState(type),
    innerType = _c[0],
    setInnerType = _c[1];
  var isPasswordVisible = React.useMemo(function () {
    return innerType !== 'password';
  }, [innerType]);
  var invalid = React.useMemo(function () {
    return state === 'invalid';
  }, [state]);
  var labelVariant = React.useMemo(function () {
    if (disabled) {
      return 'subtle';
    }
    if (invalid) {
      return 'invalid';
    }
    if (inputHasFocus) {
      return 'contrast';
    }
    return 'default';
  }, [invalid, disabled, inputHasFocus]);
  var LabelNode = React.useMemo(function () {
    if (LabelOrComponent === undefined || LabelOrComponent === null) {
      return null;
    }
    if (typeof LabelOrComponent === 'string') {
      return /*#__PURE__*/React.createElement(Label, {
        variant: labelVariant,
        htmlFor: id
      }, LabelOrComponent);
    }
    return /*#__PURE__*/React.createElement(LabelOrComponent, {
      variant: labelVariant,
      htmlFor: id
    });
  }, [LabelOrComponent, labelVariant, id]);
  var isPasswordType = React.useMemo(function () {
    return type === 'password';
  }, [type]);
  var typeOrInnerType = React.useMemo(function () {
    return isPasswordType ? innerType : type;
  }, [isPasswordType, type, innerType]);
  var hasInnerAdornment = React.useMemo(function () {
    return clearable || isPasswordType || invalid;
  }, [clearable, isPasswordType, invalid]);
  var hasAdornmentGroup = React.useMemo(function () {
    return clearable && invalid || clearable && isPasswordType || invalid && isPasswordType;
  }, [clearable, invalid, isPasswordType]);
  var clearDisabled = React.useMemo(function () {
    return readOnly || disabled;
  }, [readOnly, disabled]);
  var handleClear = React.useCallback(function () {
    var current = inputRef.current;
    if (current) {
      current.clear();
    }
  }, [inputRef]);
  var handleFocus = React.useCallback(function (e) {
    if (onFocus) {
      onFocus(e);
    }
    setInputHasFocus(true);
  }, [onFocus, setInputHasFocus]);
  var handleBlur = React.useCallback(function (e) {
    if (onBlur) {
      onBlur(e);
    }
    setInputHasFocus(false);
  }, [onBlur, setInputHasFocus]);
  var togglePasswordVisibility = React.useCallback(function () {
    setInnerType(function (prevInnerType) {
      return prevInnerType === 'password' ? undefined : 'password';
    });
  }, [setInnerType]);
  var EndAdornmentWrapper = React.useMemo(function () {
    return hasAdornmentGroup ? AdornmentGroup : React.Fragment;
  }, [hasAdornmentGroup]);
  var PasswordVisibilityToggleIcon = React.useMemo(function () {
    return isPasswordVisible ? StyledEyeClosedIcon : StyledEyeOpenIcon;
  }, [isPasswordVisible]);
  var passwordAction = React.useMemo(function () {
    return isPasswordVisible ? 'Hide password' : 'Show password';
  }, [isPasswordVisible]);
  return /*#__PURE__*/React.createElement(Box, {
    css: css
  }, LabelNode, /*#__PURE__*/React.createElement(Input, __assign({
    id: id,
    ref: inputRef,
    endAdornment: hasInnerAdornment && /*#__PURE__*/React.createElement(EndAdornmentWrapper, null, invalid && /*#__PURE__*/React.createElement(StyledExclamationTriangleIcon, {
      role: "alert",
      "aria-label": "Invalid"
    }), isPasswordType && /*#__PURE__*/React.createElement(Tooltip, {
      content: passwordAction
    }, /*#__PURE__*/React.createElement(PasswordVisibilityToggleIcon, {
      "aria-label": passwordAction,
      onClick: togglePasswordVisibility
    })), clearable && !clearDisabled && /*#__PURE__*/React.createElement(Tooltip, {
      content: "Clear"
    }, /*#__PURE__*/React.createElement(StyledCrossCircledIcon, {
      "aria-label": "Clear",
      onClick: handleClear
    }))),
    state: state,
    type: typeOrInnerType,
    disabled: disabled,
    readOnly: readOnly,
    onFocus: handleFocus,
    onBlur: handleBlur
  }, props)));
});

export { TextField };
